import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useSessionStore } from './stores/session';

/**
 * Routes.
 *
 * `/p/:code` is the scanned-tag entry point — the URL printed on every physical
 * QR tag. It resolves a *position*, never a tree, so a replanted position keeps
 * working with the same tag forever (dev brief §4.5).
 */
const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('./views/LoginView.vue'),
    meta: { public: true },
  },
  {
    path: '/p/:code',
    name: 'scan',
    component: () => import('./views/ScanResolveView.vue'),
    // Public: an unauthenticated scan gets the read-only tree card, which is
    // also the shape a future investor view takes (§4.5, UI brief §7.17).
    meta: { public: true },
  },
  {
    path: '/',
    component: () => import('./views/AppShell.vue'),
    children: [
      { path: '', redirect: { name: 'dashboard' } },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('./views/DashboardView.vue'),
      },
      {
        path: 'positions',
        name: 'positions',
        component: () => import('./views/PositionsView.vue'),
      },
      {
        path: 'positions/new',
        name: 'position-new',
        component: () => import('./views/PositionNewView.vue'),
      },
      {
        path: 'positions/:id',
        name: 'position',
        component: () => import('./views/PositionDetailView.vue'),
      },
      {
        path: 'trees/:id',
        name: 'tree',
        component: () => import('./views/TreeDetailView.vue'),
      },
      {
        path: 'settings',
        name: 'site-settings',
        component: () => import('./views/SiteSettingsView.vue'),
      },
      {
        path: 'issues',
        name: 'issues',
        component: () => import('./views/IssuesView.vue'),
      },
      {
        path: 'reports',
        name: 'reports',
        component: () => import('./views/ReportsView.vue'),
      },
      {
        path: 'work-orders',
        name: 'work-orders',
        component: () => import('./views/WorkOrdersView.vue'),
      },
      {
        path: 'work-orders/new',
        name: 'work-order-new',
        component: () => import('./views/WorkOrderNewView.vue'),
      },
      {
        path: 'work-orders/:id',
        name: 'work-order',
        component: () => import('./views/WorkOrderDetailView.vue'),
      },
    ],
  },
  {
    // Outside the shell on purpose: §7.9 allows no chrome above 64px and gives
    // the grid the full width, so the sidebar must not wrap it.
    path: '/work-orders/:id/entry',
    name: 'work-order-entry',
    component: () => import('./views/WorkOrderEntryView.vue'),
  },
  { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('./views/NotFoundView.vue') },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
});

router.beforeEach(async (to) => {
  const session = useSessionStore();

  // Wait for the boot-time refresh to settle, or a hard reload onto a protected
  // route bounces to login before the session has had a chance to restore.
  if (!session.ready) await session.restore();

  if (to.meta.public) return true;
  if (session.isAuthenticated) return true;

  return { name: 'login', query: { redirect: to.fullPath } };
});
